import type { AuthUser, TokenResponse } from "./types";

const AUTH_SERVICE_URL = process.env.NEXT_PUBLIC_AUTH_SERVICE_URL || "http://localhost:9000";

export async function loginWithCredentials(email: string, password: string): Promise<TokenResponse> {
  const formData = new URLSearchParams();
  formData.append("username", email);
  formData.append("password", password);

  const response = await fetch(`${AUTH_SERVICE_URL}/api/v2/auth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Login failed");
  }

  return response.json();
}

export async function refreshTokens(refreshToken: string): Promise<TokenResponse> {
  const response = await fetch(`${AUTH_SERVICE_URL}/api/v2/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) throw new Error("Token refresh failed");
  return response.json();
}

export async function fetchCurrentUser(accessToken: string): Promise<AuthUser> {
  const response = await fetch(`${AUTH_SERVICE_URL}/api/v2/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) throw new Error("Failed to fetch user");
  return response.json();
}

export function getMicrosoftSSOUrl(redirectUri?: string): string {
  const params = new URLSearchParams();
  if (redirectUri) params.set("redirect_uri", redirectUri);
  return `${AUTH_SERVICE_URL}/api/v2/auth/sso/microsoft?${params}`;
}

export async function updateProfile(
  accessToken: string,
  data: { name?: string; email?: string },
): Promise<AuthUser> {
  const response = await fetch(`${AUTH_SERVICE_URL}/api/v2/auth/me`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update profile");
  }

  return response.json();
}

export async function changePassword(
  accessToken: string,
  currentPassword: string | null,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await fetch(`${AUTH_SERVICE_URL}/api/v2/auth/me/password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to change password");
  }

  return response.json();
}

export async function registerUser(email: string, password: string, name: string): Promise<TokenResponse> {
  const response = await fetch(`${AUTH_SERVICE_URL}/api/v2/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, name }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Registration failed");
  }

  return response.json();
}
